import { Node, mergeAttributes } from "@tiptap/core";

export const ResizableImage = Node.create({
  name: "resizableImage",

  group: "inline",
  inline: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: "" },
      width: { default: "auto" },
      height: { default: "auto" },
      align: { default: "center" },
      objectFit: { default: "contain" },
      objectPosition: { default: "center" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "img[src]",
        getAttrs: (dom) => ({
          src: dom.getAttribute("src"),
          alt: dom.getAttribute("alt") || "",
          width: dom.getAttribute("width") || dom.style.width || "auto",
          height: dom.getAttribute("height") || dom.style.height || "auto",
          align:
            dom.getAttribute("data-align") ||
            dom.getAttribute("align") ||
            "center",
          objectFit: dom.getAttribute("data-object-fit") || "contain",
          objectPosition: dom.getAttribute("data-object-position") || "center",
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { align, objectFit, objectPosition, width, height, ...otherAttrs } = HTMLAttributes;
    const styles = [
      `object-fit: ${objectFit}`,
      `object-position: ${objectPosition}`,
      width !== "auto" ? `width: ${width}` : "",
      height !== "auto" ? `height: ${height}` : "",
      align === "center"
        ? "margin: 0 auto; display: inline-block;"
        : align === "left"
        ? "margin: 0 auto 0 0; float: left;"
        : "margin: 0 0 0 auto; float: right;",
    ]
      .filter(Boolean)
      .join("; ");

    return [
      "img",
      mergeAttributes(otherAttrs, {
        style: styles,
        "data-align": align,
        "data-object-fit": objectFit,
        "data-object-position": objectPosition,
        width: width !== "auto" ? String(width).replace("px", "px") : null,
        height: height !== "auto" ? String(height).replace("px", "px") : null,
      }),
    ];
  },

  addCommands() {
    return {
      setResizableImage:
        (attributes) =>
        ({ commands }) => {
          const { width, height } = attributes;
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...attributes,
              width: width ? `${width}px` : "auto",
              height: height ? `${height}px` : "auto",
            },
          });
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const container = document.createElement("div");
      container.className = "resizable-image-container";
      container.style.display = "inline-block";
      container.style.position = "relative";
      container.style.lineHeight = "0";

      const img = document.createElement("img");
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || "";
        img.style.width = node.attrs.width;
      img.style.height = node.attrs.height;
      img.style.objectFit = node.attrs.objectFit;
      img.style.objectPosition = node.attrs.objectPosition;

      if (node.attrs.align === "center") {
        container.style.margin = "0 auto";
        container.style.display = "inline-block";
      } else if (node.attrs.align === "left") {
        container.style.margin = "0 auto 0 0";
        container.style.float = "left";
      } else if (node.attrs.align === "right") {
        container.style.margin = "0 0 0 auto";
        container.style.float = "right";
      }

      const handles = [
        "top-left",
        "top",
        "top-right",
        "left",
        "right",
        "bottom-left",
        "bottom",
        "bottom-right",
      ].map((position) => {
      const handle = document.createElement("div");
        handle.className = `resize-handle resize-handle-${position}`;
      handle.style.position = "absolute";
        handle.style.width = "10px";
        handle.style.height = "10px";
        handle.style.background = "#007bff";
        handle.style.borderRadius = "50%";
        handle.style.cursor =
          position.includes("top") && position.includes("left")
            ? "nwse-resize"
            : position.includes("top") && position.includes("right")
            ? "nesw-resize"
            : position.includes("bottom") && position.includes("left")
            ? "nesw-resize"
            : position.includes("bottom") && position.includes("right")
            ? "nwse-resize"
            : position === "top" || position === "bottom"
            ? "ns-resize"
            : "ew-resize";
        handle.style.zIndex = "10";
        handle.style.display = "none";

        switch (position) {
          case "top-left":
            handle.style.top = "-5px";
            handle.style.left = "-5px";
            break;
          case "top":
            handle.style.top = "-5px";
            handle.style.left = "50%";
            handle.style.transform = "translateX(-50%)";
            break;
          case "top-right":
            handle.style.top = "-5px";
            handle.style.right = "-5px";
            break;
          case "left":
            handle.style.top = "50%";
            handle.style.left = "-5px";
            handle.style.transform = "translateY(-50%)";
            break;
          case "right":
            handle.style.top = "50%";
            handle.style.right = "-5px";
            handle.style.transform = "translateY(-50%)";
            break;
          case "bottom-left":
            handle.style.bottom = "-5px";
            handle.style.left = "-5px";
            break;
          case "bottom":
            handle.style.bottom = "-5px";
            handle.style.left = "50%";
            handle.style.transform = "translateX(-50%)";
            break;
          case "bottom-right":
            handle.style.bottom = "-5px";
            handle.style.right = "-5px";
            break;
        }

        return handle;
      });

      container.appendChild(img);
      handles.forEach((handle) => container.appendChild(handle));

      container.addEventListener("click", (e) => {
        e.stopPropagation();
        document
          .querySelectorAll(".resizable-image-container.selected")
          .forEach((el) => el.classList.remove("selected"));
        container.classList.add("selected");
        handles.forEach((handle) => (handle.style.display = "inline-block"));
      });

      if (!window.__resizableImageClickListenerAdded) {
        document.addEventListener("click", () => {
          document
            .querySelectorAll(".resizable-image-container.selected")
            .forEach((el) => {
              el.classList.remove("selected");
              el.querySelectorAll(".resize-handle").forEach(
                (h) => (h.style.display = "none")
              );
            });
        });
        window.__resizableImageClickListenerAdded = true;
      }

      let isResizing = false;
      let startX, startY, startWidth, startHeight, aspectRatio;

      const updateImageSize = (newWidth, newHeight) => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos !== null) {
          editor
            .chain()
            .focus()
            .command(({ tr }) => {
              tr.setNodeMarkup(pos, null, {
                ...node.attrs,
                width: newWidth,
                height: newHeight,
              });
              return true;
            })
            .run();
        }
      };

      const onMouseDown = (e, position) => {
        e.preventDefault();
        isResizing = true;
        startX = e.clientX;
        startY = e.clientY;
        startWidth = img.getBoundingClientRect().width;
        startHeight = img.getBoundingClientRect().height;
        aspectRatio = startWidth / startHeight;

        const onMouseMove = (moveEvent) => {
          if (!isResizing) return;
          const deltaX = moveEvent.clientX - startX;
          const deltaY = moveEvent.clientY - startY;

          let newWidth = startWidth;
          let newHeight = startHeight;

          if (position.includes("top-left")) {
            newWidth = startWidth - deltaX;
            newHeight = startHeight - deltaY;
            if (!moveEvent.shiftKey) newHeight = newWidth / aspectRatio;
          } else if (position.includes("top-right")) {
            newWidth = startWidth + deltaX;
            newHeight = startHeight - deltaY;
            if (!moveEvent.shiftKey) newHeight = newWidth / aspectRatio;
          } else if (position.includes("bottom-left")) {
            newWidth = startWidth - deltaX;
            newHeight = startHeight + deltaY;
            if (!moveEvent.shiftKey) newHeight = newWidth / aspectRatio;
          } else if (position.includes("bottom-right")) {
            newWidth = startWidth + deltaX;
            newHeight = startHeight + deltaY;
            if (!moveEvent.shiftKey) newHeight = newWidth / aspectRatio;
          } else if (position === "left" || position === "right") {
            newWidth =
              position === "left" ? startWidth - deltaX : startWidth + deltaX;
          } else if (position === "top" || position === "bottom") {
            newHeight =
              position === "top" ? startHeight - deltaY : startHeight + deltaY;
          }

          newWidth = Math.max(50, newWidth);
          newHeight = Math.max(50, newHeight);

          updateImageSize(
            `${Math.round(newWidth)}px`,
            `${Math.round(newHeight)}px`
          );
        };

        const onMouseUp = () => {
          isResizing = false;
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      };

      handles.forEach((handle, index) => {
        const pos = handles[index].className
          .split(" ")[1]
          .replace("resize-handle-", "");
        handle.addEventListener("mousedown", (e) => onMouseDown(e, pos));
      });

      return {
        dom: container,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;

          const isSelected = container.classList.contains("selected");
          handles.forEach(
            (h) => (h.style.display = isSelected ? "inline-block" : "none")
          );

          img.src = updatedNode.attrs.src;
          img.alt = updatedNode.attrs.alt || "";
          img.style.width = updatedNode.attrs.width;
          img.style.height = updatedNode.attrs.height;
          img.style.objectFit = updatedNode.attrs.objectFit;
          img.style.objectPosition = updatedNode.attrs.objectPosition;

          container.style.margin =
            updatedNode.attrs.align === "center"
              ? "0 auto"
              : updatedNode.attrs.align === "left"
              ? "0 auto 0 0"
              : "0 0 0 auto";
          container.style.display =
            updatedNode.attrs.align === "center" ? "inline-block" : "inline-block";
          container.style.float =
            updatedNode.attrs.align === "left"
              ? "left"
              : updatedNode.attrs.align === "right"
              ? "right"
              : "none";

          return true;
        },
        destroy: () => {
          handles.forEach((handle) => {
            handle.removeEventListener("mousedown", onMouseDown);
          });
        },
      };
    };
  },
});

export default ResizableImage;